/**
 * Single source of truth for trip-form validation rules.
 *
 * Every visible text limit here matches the corresponding business rule in
 * the Spring backend (see `src/main/java/.../entites/trip/*.java`). The
 * formatted trip description is stored with a larger transport allowance so
 * its HTML markup does not count against the 5,000-character user limit.
 *
 * If the backend limit ever changes, update this file in the same commit
 * so frontend and backend stay in lockstep.
 *
 * Why: we hit a 409 conflict on the user's "Volta de Bicicleta..." activity
 * (57 chars vs. a 55-char @Size constraint) and a 400 with the same root
 * cause for the long accommodation description. Mirroring the rules in the
 * form lets us show a clear error *before* the user clicks "Publicar" and
 * saves a round-trip — and keeps the form populated if the publish fails.
 */

import { richTextToPlainText } from './richText';

// ──────────────────────────────────────────────────────────────────────
// 1. Monetary limits
// ──────────────────────────────────────────────────────────────────────
// Monetary values are stored as currency units with up to two decimal
// places. Both the Portuguese comma and the international dot are accepted
// while typing; the API receives a normalised numeric value.
export const MONEY_LIMITS = {
  max: 1_000_000,
  decimalPlaces: 2,
};

// ──────────────────────────────────────────────────────────────────────
// 2. Text-length limits (mirror entity @Size + @Column length)
// ──────────────────────────────────────────────────────────────────────
export const TRIP_LIMITS = {
  // Top-level trip fields
  // Must match Trip.title's @Size(max = 100) and the public API contract.
  // Keeping this limit here prevents a title that only fails after submit.
  title: 100,
  tripSummary: 500,
  tripDescription: 5000,
  weather: 100,

  // Itinerary
  itineraryDay: 55, // e.g. "Day 1" — short label only
  itineraryActivityName: 2000, // activities may contain a complete description
  itineraryActivityDescription: 2000, // bumped from 255

  // Reference points
  referencePointName: 200,
  referencePointDescription: 2000, // was text-before, now explicit limit
  referencePointCity: 100, // bumped from 55 ("San Cristóbal de La Laguna")

  // Recommended foods
  foodName: 200,
  foodDescription: 2000, // bumped from 255
  restaurantName: 200,
  foodCity: 100, // bumped from 55

  // Accommodations
  accommodationName: 55, // short hotel name
  accommodationCity: 100, // bumped from 55
  accommodationDescription: 2000, // bumped from 250
  accommodationPrice: MONEY_LIMITS.max,
  accommodationNights: 365, // sanity cap; backend accepts any int

  // Extra points
  negativePointName: 200,
  negativePointDescription: 2000,
  positivePointName: 200,
  positivePointDescription: 2000,

  // Transports
  transportDescription: 2000,
  transportCost: MONEY_LIMITS.max,
};

// ──────────────────────────────────────────────────────────────────────
// 3. File limits (mirror application.yml `app.media.*` / `app.file-storage.*`)
// ──────────────────────────────────────────────────────────────────────
export const FILE_LIMITS = {
  // app.file-storage.max-file-size
  maxImageSizeBytes: 20 * 1024 * 1024, // 20 MB (backend hard limit)
  // spring.servlet.multipart.max-file-size / LocalFileStorageService
  // (kept in sync with the production Nginx request limit)
  maxVideoSizeBytes: 200 * 1024 * 1024, // 200 MB
  maxVideoDurationSeconds: 60, // 1 minute

  // app.media.*
  maxPhotosPerTrip: 20,
  // Three short videos keep a trip rich without turning a mobile publish
  // into several minutes of upstream transfer and VPS transcoding.
  maxVideosPerTrip: 3,
  maxTotalVideoSizeBytes: 600 * 1024 * 1024,
  maxPhotosPerAccommodation: 5,
  maxPhotosPerReferencePoint: 5,
  // Backend enforces "max 1 photo per food" by failing on the second upload.
  maxPhotosPerFood: 1,

  // Browser input formats. HEIC/HEIF are converted to JPEG before the
  // request, because the backend stores browser-compatible raster formats.
  imageExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'],
  // matches LocalFileStorageService.isValidVideoMimeType
  videoExtensions: ['mp4', 'webm', 'mov', 'avi', 'mkv'],
};

// Multidestination trips get a small media allowance per route stop. Keep
// single-destination trips on the original caps and add the allowance only
// when the trip actually has more than one destination.
export const getTripMediaLimits = (destinationCount) => {
  const count = Number(destinationCount);
  const extraDestinations = Number.isInteger(count) && count > 1 ? count : 0;
  return {
    ...FILE_LIMITS,
    maxPhotosPerTrip: FILE_LIMITS.maxPhotosPerTrip + (extraDestinations * 3),
    maxVideosPerTrip: FILE_LIMITS.maxVideosPerTrip + extraDestinations,
  };
};

// ──────────────────────────────────────────────────────────────────────
// 4. Small helpers
// ──────────────────────────────────────────────────────────────────────

/** Returns true when the input is a non-empty value.
 *  Accepts strings ("hello") AND numbers (1, 2, …) so that fields
 *  whose values are stored as numbers in React state (e.g. itinerary
 *  day parsed by parseInt) still pass the required check.
 *  Returns false for: null, undefined, '', '   ', NaN, Infinity,
 *  and 0 (since 0 is rarely a meaningful "set" value — parseInt("")
 *  returns NaN, not 0, so users will never legitimately set 0). */
const isNonEmpty = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return Number.isFinite(v) && v !== 0;
  return false;
};

/** Length in JS characters (not bytes). Matches Hibernate @Size semantics. */
const charLen = (v) => (typeof v === 'string' ? v.length : 0);

export const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '?';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileExt = (name) => {
  if (!name || typeof name !== 'string') return '';
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.substring(i + 1).toLowerCase() : '';
};

// ──────────────────────────────────────────────────────────────────────
// 5. Single-field validators
//    Each returns null if valid, or a human-readable error string.
// ──────────────────────────────────────────────────────────────────────

export const validateText = (value, { label, max, required = false } = {}) => {
  const len = charLen(value);
  if (required && !isNonEmpty(value)) {
    return `${label} é obrigatório.`;
  }
  if (max && len > max) {
    return `${label} excede o máximo de ${max} caracteres (atualmente ${len}).`;
  }
  return null;
};

/**
 * Validate a money field before it is converted to a JavaScript number.
 * The lexical check is important: Number('1e21') is finite and would
 * otherwise make it through the UI. A single comma or dot is accepted, but
 * scientific notation and mixed separators are rejected.
 */

export const validateMoney = (
  value,
  {
    label = 'Valor',
    min = 0,
    max = MONEY_LIMITS.max,
    decimalPlaces = MONEY_LIMITS.decimalPlaces,
    required = false,
  } = {},
) => {
  const raw = value === '' || value == null ? '' : String(value).trim();
  if (raw === '') {
    return required ? `${label} é obrigatório.` : null;
  }
  if (/[eE]/.test(raw)) {
    return `${label} não pode usar notação científica.`;
  }
  if (!/^-?\d+(?:[.,]\d+)?$/.test(raw)) {
    return `${label} tem de ser um número válido.`;
  }

  const normalized = raw.replace(',', '.');
  const fraction = normalized.split('.')[1] || '';
  if (fraction.length > decimalPlaces) {
    if (decimalPlaces === 0) {
      return `${label} não pode ter casas decimais; usa um valor inteiro.`;
    }
    return `${label} pode ter no máximo ${decimalPlaces} casas decimais.`;
  }

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) {
    return `${label} tem de ser um número finito.`;
  }
  if (numeric < min) {
    return `${label} tem de ser >= ${min}.`;
  }
  if (max != null && numeric > max) {
    return `${label} não pode exceder ${max.toLocaleString('pt-PT')}.`;
  }
  return null;
};

/** Parse only a value that satisfies validateMoney; invalid values become null. */
export const parseMoney = (value, options = {}) => {
  if (validateMoney(value, options)) return null;
  if (value === '' || value == null) return null;
  return Number(String(value).trim().replace(',', '.'));
};

/** Round/sum money without exposing binary floating-point artefacts. */
export const roundMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};

export const sumMoney = (...values) => roundMoney(values.reduce((sum, value) => {
  const parsed = parseMoney(value);
  return sum + (parsed == null ? 0 : parsed);
}, 0));

/** Display money in the user's Portuguese locale, retaining meaningful cents. */
export const formatMoney = (value, { maximumFractionDigits = MONEY_LIMITS.decimalPlaces } = {}) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return new Intl.NumberFormat('pt-PT', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(roundMoney(numeric));
};

/**
 * Validate a single File (image or video) against the trip's storage rules.
 * Mirrors the LocalFileStorageService.validateImage / validateVideo.
 */
export const validateFile = (file, kind = 'image') => {
  if (!(file instanceof File) && !(file instanceof Blob)) {
    return `${kind === 'video' ? 'Vídeo' : 'Imagem'} inválido.`;
  }
  const ext = fileExt(file.name);
  if (kind === 'image') {
    if (!FILE_LIMITS.imageExtensions.includes(ext)) {
      return `Formato de imagem não suportado (${ext || 'sem extensão'}). Use: ${FILE_LIMITS.imageExtensions.join(', ')}.`;
    }
    if (file.size > FILE_LIMITS.maxImageSizeBytes) {
      return `Imagem demasiado grande (${formatBytes(file.size)}). Máximo: ${formatBytes(FILE_LIMITS.maxImageSizeBytes)}.`;
    }
  } else {
    if (!FILE_LIMITS.videoExtensions.includes(ext)) {
      return `Formato de vídeo não suportado (${ext || 'sem extensão'}). Use: ${FILE_LIMITS.videoExtensions.join(', ')}.`;
    }
    if (file.size > FILE_LIMITS.maxVideoSizeBytes) {
      return `Vídeo demasiado grande (${formatBytes(file.size)}). Máximo: ${formatBytes(FILE_LIMITS.maxVideoSizeBytes)}.`;
    }
  }
  return null;
};

/**
 * Validate a numeric input that maps to a backend `int` field.
 * Returns null if valid, otherwise an error string.
 */
export const validateInt = (value, { label, min = 0, max, required = false } = {}) => {
  const raw = value === '' || value == null ? '' : String(value);
  if (raw === '') {
    return required ? `${label} é obrigatório.` : null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return `${label} tem de ser um número inteiro.`;
  }
  if (n < min) {
    return `${label} tem de ser >= ${min}.`;
  }
  if (max != null && n > max) {
    return `${label} tem de ser <= ${max}.`;
  }
  return null;
};

/**
 * Validate a date in `YYYY-MM-DD` format. Returns null if valid.
 * `compareTo` (optional) is { min, max } against today or another date string.
 */
export const validateDate = (value, { label, required = false, min, max } = {}) => {
  if (!value) {
    return required ? `${label} é obrigatória.` : null;
  }
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${label} tem de estar no formato AAAA-MM-DD.`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return `${label} é uma data inválida.`;
  }
  if (min && value < min) return `${label} tem de ser >= ${min}.`;
  if (max && value > max) return `${label} tem de ser <= ${max}.`;
  return null;
};

// ──────────────────────────────────────────────────────────────────────
// 6. Whole-form validator
//    Runs the cheap, client-side checks so we don't ping the backend
//    for a payload we already know is bad.
// ──────────────────────────────────────────────────────────────────────

/**
 * The shape of a single error from `validateTripForm`:
 *
 *   {
 *     section: 'general' | 'accommodations' | 'referencePoints' |
 *              'foods' | 'positivePoints' | 'negativePoints' |
 *              'itinerary' | 'media',
 *     sectionLabel: 'Informações Gerais',            // user-friendly
 *     itemIndex: 2,                                  // 0-based index of
 *                                                    // the offending item
 *                                                    // (e.g. accom #3);
 *                                                    // null for top-level
 *     itemLabel: 'Estadia #3',                       // display name
 *     field: 'description',                           // which sub-field
 *                                                    // is bad (null for
 *                                                    // item-level)
 *     message: 'excede o máximo de 2000 caracteres...', // human msg
 *   }
 *
 * Sections map 1:1 to the React form tabs so the error modal can
 * render a "Jump to" button per group and we can drive tab badges.
 */
// Round 41 — TRIP_FORM_SECTIONS aligned with the new wizard structure:
//   • Step 1 — Destino (country + city)
//   • Step 2 — O essencial (cover, title, summary, description, rating)
//   • Step 3 — Datas e custos (start/end/total + breakdown)
//   • Step 4 — A experiência (4 SUBTABS):
//       4.1 Informações → categories, languagesSpoken
//       4.2 Locais      → referencePoints, tripItinerary
//       4.3 Galeria     → photos + photoCaptions
//       4.4 Extras      → positivePoints + negativePoints
//   • Step 5 — Rever e publicar (privacy, publish)
//
// `tab` is the route the error modal jumps to when the user clicks a
// "Jump to" button. The legacy keys (accommodations, foods, etc.) are
// kept below as ALIASES so the old FutureTravels form (if it ever
// re-surfaces) still resolves the labels correctly.
export const TRIP_FORM_SECTIONS = {
  // Round 41 — new 5-step structure
  destination: { tab: 'where', label: 'Destino', icon: '📍', step: 0 },
  essentials: { tab: 'essentials', label: 'O essencial', icon: '✨', step: 1 },
  when: { tab: 'when', label: 'Datas e custos', icon: '📅', step: 2 },
  experience: { tab: 'experience', label: 'A experiência', icon: '🌍', step: 3 },
  experienceInfo: { tab: 'experience', subtab: 'info', label: 'Informações', icon: '🏨', step: 3 },
  experienceLocais: { tab: 'experience', subtab: 'locais', label: 'Locais', icon: '📍', step: 3 },
  experienceGaleria: { tab: 'experience', subtab: 'galeria', label: 'Galeria', icon: '📷', step: 3 },
  experienceExtras: { tab: 'experience', subtab: 'extras', label: 'Extras', icon: '📝', step: 3 },
  review: { tab: 'review', label: 'Rever e publicar', icon: '🚀', step: 4 },
  // Legacy aliases — kept so the old FutureTravels/MyTravels form (if
  // still present in a hidden route) resolves the labels without errors.
  general: { tab: 'where', label: 'Destino', icon: '📍', step: 0, legacy: true },
  accommodations: { tab: 'experience', subtab: 'info', label: 'Alojamento', icon: '🏨', step: 3, legacy: true },
  referencePoints: { tab: 'experience', subtab: 'locais', label: 'Pontos de Referência', icon: '📍', step: 3, legacy: true },
  foods: { tab: 'experience', subtab: 'info', label: 'Alimentação', icon: '🍽️', step: 3, legacy: true },
  positivePoints: { tab: 'experience', subtab: 'extras', label: 'Pontos Positivos', icon: '✅', step: 3, legacy: true },
  negativePoints: { tab: 'experience', subtab: 'extras', label: 'Pontos Negativos', icon: '⚠️', step: 3, legacy: true },
  itinerary: { tab: 'experience', subtab: 'locais', label: 'Itinerário', icon: '🗓️', step: 3, legacy: true },
  media: { tab: 'experience', subtab: 'galeria', label: 'Fotos & Vídeos', icon: '📷', step: 3, legacy: true },
};

/**
 * Validate the full trip form. Returns:
 *   { valid: true,  errors: [] }
 *   { valid: false, errors: TripFormError[] }
 *
 * `form` shape (the relevant subset of the React form state):
 *   { title, tripSummary, tripDescription, startDate, endDate,
 *     bookingDate, weather, tripRating, city, country,
 *     categories, languagesSpoken, cost, accommodations, ... }
 *
 * `media` is a sibling object holding raw File arrays:
 *   { coverPhoto, generalPhotos, videos, // trip-level
 *     accommodationPhotos: [[file, file], ...],
 *     referencePointPhotos: [[file, file], ...],
 *     foodPhotos: [file, file, ...] }
 */
export const validateTripForm = (form, media = {}) => {
  // Project the form into the field names the validation rules
  // expect (see normalizeTripForm for the full mapping). After this
  // point, all field checks below operate on backend-shaped keys
  // (`title`, `tripSummary`, …).
  form = normalizeTripForm(form);

  const mediaLimits = getTripMediaLimits(
    Array.isArray(form?.destinations) ? form.destinations.length : 0,
  );

  const errors = [];

  const add = (section, itemIndex, itemLabel, field, message) => {
    const sectionMeta = TRIP_FORM_SECTIONS[section] || TRIP_FORM_SECTIONS.general;
    errors.push({
      section,
      sectionLabel: sectionMeta.label,
      sectionIcon: sectionMeta.icon,
      itemIndex,
      itemLabel,
      field,
      message,
    });
  };

  // ── General / top-level fields ────────────────────────────────
  if (!isNonEmpty(form.title)) {
    add('general', null, null, 'title', 'Nome da Viagem é obrigatório.');
  } else if (form.title.length > TRIP_LIMITS.title) {
    add('general', null, null, 'title', `Nome da Viagem excede o máximo de ${TRIP_LIMITS.title} caracteres (atual ${form.title.length}).`);
  }
  const tripSummaryText = richTextToPlainText(form.tripSummary);
  if (!isNonEmpty(tripSummaryText)) {
    add('general', null, null, 'tripSummary', 'Descrição Curta é obrigatória.');
  } else if (tripSummaryText.length > TRIP_LIMITS.tripSummary) {
    add('general', null, null, 'tripSummary', `Descrição Curta excede o máximo de ${TRIP_LIMITS.tripSummary} caracteres (atual ${tripSummaryText.length}).`);
  }
  const tripDescriptionText = richTextToPlainText(form.tripDescription);
  if (!isNonEmpty(tripDescriptionText)) {
    add('general', null, null, 'tripDescription', 'Descrição Longa é obrigatória.');
  } else if (tripDescriptionText.length > TRIP_LIMITS.tripDescription) {
    add('general', null, null, 'tripDescription', `Descrição Longa excede o máximo de ${TRIP_LIMITS.tripDescription} caracteres (atual ${tripDescriptionText.length}).`);
  }
  if (isNonEmpty(form.weather) && form.weather.length > TRIP_LIMITS.weather) {
    add('general', null, null, 'weather', `Clima excede o máximo de ${TRIP_LIMITS.weather} caracteres (atual ${form.weather.length}).`);
  }
  const rating = Number(form.stars ?? form.tripRating);
  if (rating === 0 || rating == null || Number.isNaN(rating)) {
    add('general', null, null, 'stars', 'Avaliação da viagem é obrigatória (escolha 1-5 estrelas).');
  } else if (rating < 0 || rating > 5) {
    add('general', null, null, 'stars', 'Avaliação tem de estar entre 0 e 5.');
  }

  // Dates
  if (!form.startDate) {
    add('general', null, null, 'startDate', 'Data de Início é obrigatória.');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(form.startDate) || Number.isNaN(new Date(form.startDate).getTime())) {
    add('general', null, null, 'startDate', 'Data de Início tem de estar no formato AAAA-MM-DD.');
  }
  if (!form.endDate) {
    add('general', null, null, 'endDate', 'Data de Fim é obrigatória.');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(form.endDate) || Number.isNaN(new Date(form.endDate).getTime())) {
    add('general', null, null, 'endDate', 'Data de Fim tem de estar no formato AAAA-MM-DD.');
  }
  if (form.startDate && form.endDate && form.startDate > form.endDate) {
    add('general', null, null, 'endDate', 'Data de início tem de ser anterior à data de fim.');
  }

  if (!isNonEmpty(form.country)) add('general', null, null, 'country', 'País é obrigatório.');
  if (!isNonEmpty(form.city)) add('general', null, null, 'city', 'Cidade é obrigatória.');

  if (!Array.isArray(form.category) || form.category.length === 0) {
    add('general', null, null, 'category', 'Selecione pelo menos uma categoria.');
  }
  if (!Array.isArray(form.languages) || form.languages.length === 0) {
    add('general', null, null, 'languages', 'Selecione pelo menos uma língua.');
  }

  // Cost sub-totals. Validate the raw string first so exponent notation,
  // decimals and values above the product limit are never normalised into
  // a plausible-looking number.
  const pd = form.priceDetails || {};
  const costLabels = {
    hotel: 'Alojamento',
    flight: 'Voo / Transporte',
    food: 'Alimentação',
    transport: 'Transporte',
    extras: 'Extras',
  };
  Object.keys(costLabels).forEach((k) => {
    const v = pd[k];
    const error = validateMoney(v, { label: `Custo de ${costLabels[k]}` });
    if (error) {
      add('general', null, null, `priceDetails.${k}`, error);
    }
  });

  // ── Accommodations ─────────────────────────────────────────────
  if (Array.isArray(form.accommodations)) {
    form.accommodations.forEach((acc, i) => {
      const itemLabel = `Estadia #${i + 1}`;
      if (!isNonEmpty(acc.name)) {
        add('accommodations', i, itemLabel, 'name', 'Nome da estadia é obrigatório.');
      } else if (acc.name.length > TRIP_LIMITS.accommodationName) {
        add('accommodations', i, itemLabel, 'name', `Nome excede o máximo de ${TRIP_LIMITS.accommodationName} caracteres (atual ${acc.name.length}).`);
      }
      if (!isNonEmpty(acc.city)) {
        add('accommodations', i, itemLabel, 'city', 'Cidade é obrigatória.');
      } else if (acc.city.length > TRIP_LIMITS.accommodationCity) {
        add('accommodations', i, itemLabel, 'city', `Cidade excede o máximo de ${TRIP_LIMITS.accommodationCity} caracteres (atual ${acc.city.length}).`);
      }
      if (!isNonEmpty(acc.description)) {
        add('accommodations', i, itemLabel, 'description', 'Descrição é obrigatória.');
      } else if (acc.description.length > TRIP_LIMITS.accommodationDescription) {
        add('accommodations', i, itemLabel, 'description', `Descrição excede o máximo de ${TRIP_LIMITS.accommodationDescription} caracteres (atual ${acc.description.length}).`);
      }
      const priceError = validateMoney(acc.price, {
        label: 'Preço',
        max: TRIP_LIMITS.accommodationPrice,
      });
      if (priceError) {
        add('accommodations', i, itemLabel, 'price', priceError);
      }
      const nights = Number(acc.nights);
      if (acc.nights == null || acc.nights === '' || Number.isNaN(nights) || nights < 0) {
        add('accommodations', i, itemLabel, 'nights', 'Número de noites tem de ser >= 0.');
      } else if (nights > TRIP_LIMITS.accommodationNights) {
        add('accommodations', i, itemLabel, 'nights', `Número de noites excede o máximo razoável de ${TRIP_LIMITS.accommodationNights}.`);
      }
    });
  }

  // ── Reference points ──────────────────────────────────────────
  if (Array.isArray(form.pointsOfInterest)) {
    form.pointsOfInterest.forEach((p, i) => {
      const itemLabel = `Ponto #${i + 1}`;
      if (!isNonEmpty(p.name)) {
        add('referencePoints', i, itemLabel, 'name', 'Nome do ponto de referência é obrigatório.');
      } else if (p.name.length > TRIP_LIMITS.referencePointName) {
        add('referencePoints', i, itemLabel, 'name', `Nome excede o máximo de ${TRIP_LIMITS.referencePointName} caracteres (atual ${p.name.length}).`);
      }
      if (isNonEmpty(p.description) && p.description.length > TRIP_LIMITS.referencePointDescription) {
        add('referencePoints', i, itemLabel, 'description', `Descrição excede o máximo de ${TRIP_LIMITS.referencePointDescription} caracteres (atual ${p.description.length}).`);
      }
      if (isNonEmpty(p.city) && p.city.length > TRIP_LIMITS.referencePointCity) {
        add('referencePoints', i, itemLabel, 'city', `Cidade excede o máximo de ${TRIP_LIMITS.referencePointCity} caracteres (atual ${p.city.length}).`);
      }
    });
  }

  // ── Foods ──────────────────────────────────────────────────────
  if (Array.isArray(form.foodRecommendations)) {
    form.foodRecommendations.forEach((f, i) => {
      const itemLabel = `Recomendação #${i + 1}`;
      if (!isNonEmpty(f.name)) {
        add('foods', i, itemLabel, 'name', 'Nome da recomendação é obrigatório.');
      } else if (f.name.length > TRIP_LIMITS.foodName) {
        add('foods', i, itemLabel, 'name', `Nome excede o máximo de ${TRIP_LIMITS.foodName} caracteres (atual ${f.name.length}).`);
      }
      if (isNonEmpty(f.description) && f.description.length > TRIP_LIMITS.foodDescription) {
        add('foods', i, itemLabel, 'description', `Descrição excede o máximo de ${TRIP_LIMITS.foodDescription} caracteres (atual ${f.description.length}).`);
      }
      if (isNonEmpty(f.restaurantName) && f.restaurantName.length > TRIP_LIMITS.restaurantName) {
        add('foods', i, itemLabel, 'restaurantName', `Nome do restaurante excede o máximo de ${TRIP_LIMITS.restaurantName} caracteres (atual ${f.restaurantName.length}).`);
      }
      if (isNonEmpty(f.city) && f.city.length > TRIP_LIMITS.foodCity) {
        add('foods', i, itemLabel, 'city', `Cidade excede o máximo de ${TRIP_LIMITS.foodCity} caracteres (atual ${f.city.length}).`);
      }
    });
  }

  // ── Negative points ───────────────────────────────────────────
  if (Array.isArray(form.negativePoints)) {
    form.negativePoints.forEach((p, i) => {
      const itemLabel = `Ponto Negativo #${i + 1}`;
      if (!isNonEmpty(p.name)) {
        add('negativePoints', i, itemLabel, 'name', 'Nome é obrigatório.');
      } else if (p.name.length > TRIP_LIMITS.negativePointName) {
        add('negativePoints', i, itemLabel, 'name', `Nome excede o máximo de ${TRIP_LIMITS.negativePointName} caracteres (atual ${p.name.length}).`);
      }
      if (isNonEmpty(p.description) && p.description.length > TRIP_LIMITS.negativePointDescription) {
        add('negativePoints', i, itemLabel, 'description', `Descrição excede o máximo de ${TRIP_LIMITS.negativePointDescription} caracteres (atual ${p.description.length}).`);
      }
    });
  }

  // ── Positive points ───────────────────────────────────────────
  if (Array.isArray(form.positivePoints)) {
    form.positivePoints.forEach((p, i) => {
      const itemLabel = `Ponto Positivo #${i + 1}`;
      if (!isNonEmpty(p.name)) {
        add('positivePoints', i, itemLabel, 'name', 'Nome é obrigatório.');
      } else if (p.name.length > TRIP_LIMITS.positivePointName) {
        add('positivePoints', i, itemLabel, 'name', `Nome excede o máximo de ${TRIP_LIMITS.positivePointName} caracteres (atual ${p.name.length}).`);
      }
      if (isNonEmpty(p.description) && p.description.length > TRIP_LIMITS.positivePointDescription) {
        add('positivePoints', i, itemLabel, 'description', `Descrição excede o máximo de ${TRIP_LIMITS.positivePointDescription} caracteres (atual ${p.description.length}).`);
      }
    });
  }

  // ── Itinerary ─────────────────────────────────────────────────
  if (Array.isArray(form.itinerary)) {
    form.itinerary.forEach((day, di) => {
      const dayLabel = `Dia ${day.day || di + 1}`;
      if (!isNonEmpty(day.day)) {
        add('itinerary', di, dayLabel, 'day', 'Número do dia é obrigatório.');
      } else if (String(day.day).length > TRIP_LIMITS.itineraryDay) {
        add('itinerary', di, dayLabel, 'day', `Nome do dia excede o máximo de ${TRIP_LIMITS.itineraryDay} caracteres.`);
      }
      if (Array.isArray(day.activities)) {
        day.activities.forEach((a, ai) => {
          if (!isNonEmpty(a)) return;
          if (a.length > TRIP_LIMITS.itineraryActivityName) {
            add('itinerary', di, dayLabel, `activities[${ai}]`, `Atividade #${ai + 1} excede ${TRIP_LIMITS.itineraryActivityName} caracteres (atual ${a.length}).`);
          }
        });
      }
    });
  }

  // ── Media files ───────────────────────────────────────────────
  const totalTripPhotos =
    (media.coverPhoto ? 1 : 0) +
    (Array.isArray(media.generalPhotos) ? media.generalPhotos.length : 0) +
    (Array.isArray(media.accommodationPhotos) ? media.accommodationPhotos.flat().length : 0) +
    (Array.isArray(media.referencePointPhotos) ? media.referencePointPhotos.flat().length : 0) +
    (Array.isArray(media.foodPhotos) ? media.foodPhotos.length : 0);
  if (totalTripPhotos > mediaLimits.maxPhotosPerTrip) {
    add('media', null, null, 'photos', `Total de fotos (${totalTripPhotos}) excede o máximo de ${mediaLimits.maxPhotosPerTrip} por viagem.`);
  }
  const totalTripVideos = Array.isArray(media.videos) ? media.videos.length : 0;
  if (totalTripVideos > mediaLimits.maxVideosPerTrip) {
    add('media', null, null, 'videos', `Total de vídeos (${totalTripVideos}) excede o máximo de ${mediaLimits.maxVideosPerTrip} por viagem.`);
  }
  const totalVideoBytes = Array.isArray(media.videos)
    ? media.videos.reduce((sum, file) => sum + (Number(file?.size) || 0), 0)
    : 0;
  if (totalVideoBytes > FILE_LIMITS.maxTotalVideoSizeBytes) {
    add('media', null, null, 'videos', `Os vídeos excedem ${formatBytes(FILE_LIMITS.maxTotalVideoSizeBytes)} no total.`);
  }

  if (Array.isArray(media.accommodationPhotos)) {
    media.accommodationPhotos.forEach((arr, i) => {
      const n = Array.isArray(arr) ? arr.length : 0;
      if (n > FILE_LIMITS.maxPhotosPerAccommodation) {
        add('media', i, `Estadia #${i + 1}`, 'photos', `${n} fotos excedem o máximo de ${FILE_LIMITS.maxPhotosPerAccommodation} por estadia.`);
      }
      if (Array.isArray(arr)) {
        arr.forEach((f, fi) => {
          const err = validateFile(f, 'image');
          if (err) add('media', i, `Estadia #${i + 1}`, `photo[${fi}]`, err);
        });
      }
    });
  }
  if (Array.isArray(media.referencePointPhotos)) {
    media.referencePointPhotos.forEach((arr, i) => {
      const n = Array.isArray(arr) ? arr.length : 0;
      if (n > FILE_LIMITS.maxPhotosPerReferencePoint) {
        add('media', i, `Ponto #${i + 1}`, 'photos', `${n} fotos excedem o máximo de ${FILE_LIMITS.maxPhotosPerReferencePoint} por ponto.`);
      }
      if (Array.isArray(arr)) {
        arr.forEach((f, fi) => {
          const err = validateFile(f, 'image');
          if (err) add('media', i, `Ponto #${i + 1}`, `photo[${fi}]`, err);
        });
      }
    });
  }
  if (Array.isArray(media.foodPhotos)) {
    media.foodPhotos.forEach((f, i) => {
      if (!f) return;
      const err = validateFile(f, 'image');
      if (err) add('media', i, `Recomendação #${i + 1}`, 'photo', err);
    });
  }
  if (media.coverPhoto) {
    const err = validateFile(media.coverPhoto, 'image');
    if (err) add('media', null, null, 'coverPhoto', err);
  }
  if (Array.isArray(media.generalPhotos)) {
    media.generalPhotos.forEach((f, i) => {
      const err = validateFile(f, 'image');
      if (err) add('media', null, null, `generalPhoto[${i}]`, err);
    });
  }
  if (Array.isArray(media.videos)) {
    media.videos.forEach((f, i) => {
      const err = validateFile(f, 'video');
      if (err) add('media', null, null, `video[${i}]`, err);
    });
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Group structured errors by `section` for rendering. Returns:
 *   [
 *     { section, sectionLabel, sectionIcon, errors: [...] },
 *     ...
 *   ]
 * Sections are ordered the same way they appear in the form, so the
 * modal reads top-to-bottom like the page.
 */
export const groupErrorsBySection = (errors) => {
  const order = ['general', 'accommodations', 'referencePoints', 'foods', 'positivePoints', 'negativePoints', 'itinerary', 'media'];
  const buckets = new Map();
  for (const e of errors) {
    if (!buckets.has(e.section)) buckets.set(e.section, []);
    buckets.get(e.section).push(e);
  }
  return order
    .filter((k) => buckets.has(k))
    .map((k) => ({
      section: k,
      sectionLabel: TRIP_FORM_SECTIONS[k].label,
      sectionIcon: TRIP_FORM_SECTIONS[k].icon,
      tab: TRIP_FORM_SECTIONS[k].tab,
      errors: buckets.get(k),
    }));
};

/**
 * Count errors per section. Used to drive the red badges on the tab
 * buttons. Returns { general: 0, accommodations: 2, ... }.
 */
export const countErrorsBySection = (errors) => {
  const counts = {};
  for (const e of errors) {
    counts[e.section] = (counts[e.section] || 0) + 1;
  }
  return counts;
};

/**
 * Look up the error (if any) for a specific (section, field, itemIndex)
 * triple. Used by the inline-error UI so each field can ask "am I in
 * trouble?" and render its own red border + message.
 *
 * Returns the first matching error object, or null.
 *
 *   getFieldError(errors, { section: 'general', field: 'title' })
 *   getFieldError(errors, { section: 'accommodations', field: 'name', itemIndex: 0 })
 *   getFieldError(errors, { section: 'itinerary', field: 'activities[0]', itemIndex: 0 })
 */
export const getFieldError = (errors, { section, field, itemIndex = null }) => {
  return errors.find((e) =>
    e.section === section
    && e.field === field
    && (itemIndex == null || e.itemIndex === itemIndex)
  ) || null;
};

/**
 * Map frontend form values into a backend-friendly date string.
 * The HTML `<input type="date">` already returns "YYYY-MM-DD" but if
 * anyone hands us a "DD/MM/YYYY" string we normalise it. Returns
 * `null` for unparseable values.
 */
export const normaliseDateString = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw !== 'string') return null;
  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // DD/MM/YYYY (or DD-MM-YYYY)
  const m = raw.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
};

/**
 * The MyTravels.js form state uses its own field names (e.g. `name`,
 * `description`, `longDescription`, `climate`) that don't match the
 * backend DTO field names (`title`, `tripSummary`, `tripDescription`,
 * `weather`). Before validating, we project the form into a
 * validation-friendly shape so the same code path works for both
 * backend and frontend.
 *
 * Without this normalisation, errors fire for fields that the user
 * has actually filled (e.g. `form.title` is undefined when the
 * frontend stores it as `form.name`), and the user sees phantom
 * errors in the tab badges.
 *
 * Returns a NEW object — the original is left untouched so React's
 * state semantics are preserved.
 */
export const normalizeTripForm = (form) => {
  if (!form || typeof form !== 'object') return {};
  const pd = form.priceDetails || {};
  // The itinerary has the form shape [{ day, activities: [] }, …]
  // which already matches the backend structure. Same for the
  // accommodations / pointsOfInterest / foodRecommendations lists.
  return {
    ...form,
    // Map top-level fields to their DTO names.
    title: form.title ?? form.name ?? '',
    tripSummary: form.tripSummary ?? form.description ?? '',
    tripDescription: form.tripDescription ?? form.longDescription ?? '',
    weather: form.weather ?? form.climate ?? '',
    stars: form.stars ?? form.tripRating ?? 0,
    // Cost sub-totals (the form uses hotel/flight/food/transport/extras,
    // the cost DTO uses accommodation/food/transport/extra — there is
    // no flight in the cost, so we just skip the flight key).
    priceDetails: {
      hotel: pd.accommodation ?? pd.hotel ?? '',
      food: pd.food ?? '',
      transport: pd.transport ?? '',
      extras: pd.extra ?? pd.extras ?? '',
    },
    // Normalise dates so the regex check accepts both ISO and EU
    // display formats.
    startDate: normaliseDateString(form.startDate) || form.startDate || '',
    endDate: normaliseDateString(form.endDate) || form.endDate || '',
  };
};

